import { NODE_STATUSES, type NodeStatus } from '../../generated/nodeDataTypes'

export const TASK_COMPLETION_CHIME_STORAGE_KEY = 'dreamwright_task_completion_chime_enabled'

const ACTIVE_TASK_STATUSES: ReadonlySet<NodeStatus> = new Set([
  'running',
  'pending_execution',
  'pending_cost_confirmation',
])

type AudioContextConstructor = typeof AudioContext

declare global {
  interface Window {
    AudioContext?: AudioContextConstructor
    webkitAudioContext?: AudioContextConstructor
  }
}

export interface TaskCompletionNodeStatus {
  id: string
  type?: string | null
  status?: string | NodeStatus
}

export interface TaskCompletionSnapshot {
  activeNodeIds: string[]
  completedNodeIds: string[]
  nodeStatusById: Record<string, string | null>
  nodeTypeById: Record<string, string | null>
  queuedCount: number
  activeWorkCount: number
}

export interface TaskCompletionChimeResult {
  status: 'ready' | 'played' | 'unsupported' | 'blocked'
  reason?: string
}

let taskCompletionAudioContext: AudioContext | null = null

export function taskCompletionNodeStatus(status: string | undefined): NodeStatus | undefined {
  return NODE_STATUSES.find((candidate) => candidate === status)
}

export function isTaskCompletionActiveStatus(status: string | undefined): status is NodeStatus {
  const nodeStatus = taskCompletionNodeStatus(status)
  return nodeStatus !== undefined && ACTIVE_TASK_STATUSES.has(nodeStatus)
}

export function createTaskCompletionSnapshot(
  nodes: TaskCompletionNodeStatus[],
  queuedCount = 0,
): TaskCompletionSnapshot {
  const activeNodeIds: string[] = []
  const completedNodeIds: string[] = []
  const nodeStatusById: Record<string, string | null> = {}
  const nodeTypeById: Record<string, string | null> = {}

  for (const node of nodes) {
    const status = taskCompletionNodeStatus(node.status)
    nodeStatusById[node.id] = status ?? null
    nodeTypeById[node.id] = node.type ?? null
    if (isTaskCompletionActiveStatus(status)) {
      activeNodeIds.push(node.id)
    } else if (status === 'completed') {
      completedNodeIds.push(node.id)
    }
  }

  return {
    activeNodeIds,
    completedNodeIds,
    nodeStatusById,
    nodeTypeById,
    queuedCount: Math.max(0, queuedCount),
    activeWorkCount: activeNodeIds.length + Math.max(0, queuedCount),
  }
}

export function completedNodeIdsFromPreviousActive(
  previous: TaskCompletionSnapshot,
  current: TaskCompletionSnapshot,
): string[] {
  const previousActiveNodeIds = new Set(previous.activeNodeIds)
  return current.completedNodeIds.filter((nodeId) => previousActiveNodeIds.has(nodeId))
}

function getAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null
  return window.AudioContext ?? window.webkitAudioContext ?? null
}

async function getRunningAudioContext(): Promise<TaskCompletionChimeResult | AudioContext> {
  const AudioContextCtor = getAudioContextConstructor()
  if (!AudioContextCtor) {
    return { status: 'unsupported', reason: 'audio-context-unavailable' }
  }

  try {
    taskCompletionAudioContext ??= new AudioContextCtor()
    if (taskCompletionAudioContext.state === 'suspended') {
      await taskCompletionAudioContext.resume()
    }
    if (taskCompletionAudioContext.state !== 'running') {
      return { status: 'blocked', reason: `audio-context-${taskCompletionAudioContext.state}` }
    }
    return taskCompletionAudioContext
  } catch (error) {
    return {
      status: 'blocked',
      reason: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function primeTaskCompletionChime(): Promise<TaskCompletionChimeResult> {
  const contextOrResult = await getRunningAudioContext()
  if ('status' in contextOrResult) return contextOrResult
  return { status: 'ready' }
}

export async function playTaskCompletionChime(): Promise<TaskCompletionChimeResult> {
  const contextOrResult = await getRunningAudioContext()
  if ('status' in contextOrResult) return contextOrResult

  const context = contextOrResult
  try {
    const startedAt = context.currentTime
    const gain = context.createGain()
    gain.gain.setValueAtTime(0.0001, startedAt)
    gain.gain.exponentialRampToValueAtTime(0.055, startedAt + 0.018)
    gain.gain.exponentialRampToValueAtTime(0.0001, startedAt + 0.34)
    gain.connect(context.destination)

    const root = context.createOscillator()
    root.type = 'sine'
    root.frequency.setValueAtTime(880, startedAt)
    root.frequency.exponentialRampToValueAtTime(1174.66, startedAt + 0.16)

    const overtone = context.createOscillator()
    overtone.type = 'triangle'
    overtone.frequency.setValueAtTime(1320, startedAt + 0.035)

    root.connect(gain)
    overtone.connect(gain)

    root.start(startedAt)
    root.stop(startedAt + 0.34)
    overtone.start(startedAt + 0.035)
    overtone.stop(startedAt + 0.24)
    root.onended = () => {
      root.disconnect()
      overtone.disconnect()
      gain.disconnect()
    }

    return { status: 'played' }
  } catch (error) {
    return {
      status: 'blocked',
      reason: error instanceof Error ? error.message : String(error),
    }
  }
}
